import type {
  CommentModel,
  PaginatedCommentsModel,
} from "@/services/api/comment/models/comment.model.types"

/**
 * Inserts a reply into a comment tree by finding the target parent.
 * Returns the original reference if the subtree contains no match (structural sharing).
 */
export function insertReplyInTree(
  comment: CommentModel,
  parentId: string,
  reply: CommentModel,
): CommentModel {
  if (comment.id === parentId) {
    return {
      ...comment,
      replies: {
        ...comment.replies,
        comments: [...comment.replies.comments, reply],
      },
    }
  }

  if (comment.replies.comments.length === 0) {
    return comment
  }

  const updatedReplies = comment.replies.comments.map((c) =>
    insertReplyInTree(c, parentId, reply),
  )

  if (
    updatedReplies.every(
      (updated, i) => updated === comment.replies.comments[i],
    )
  ) {
    return comment
  }

  return {
    ...comment,
    replies: {
      ...comment.replies,
      comments: updatedReplies,
    },
  }
}

/**
 * Appends a page of replies to a specific comment in the tree,
 * updating its pagination cursor.
 */
export function appendRepliesToComment(
  comment: CommentModel,
  targetId: string,
  paginated: PaginatedCommentsModel,
): CommentModel {
  if (comment.id === targetId) {
    return {
      ...comment,
      replies: {
        comments: [...comment.replies.comments, ...paginated.comments],
        endCursor: paginated.endCursor,
        hasNextPage: paginated.hasNextPage,
      },
    }
  }

  if (comment.replies.comments.length === 0) {
    return comment
  }

  const updatedReplies = comment.replies.comments.map((c) =>
    appendRepliesToComment(c, targetId, paginated),
  )

  if (
    updatedReplies.every(
      (updated, i) => updated === comment.replies.comments[i],
    )
  ) {
    return comment
  }

  return {
    ...comment,
    replies: {
      ...comment.replies,
      comments: updatedReplies,
    },
  }
}
